const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const SWIPE_THRESHOLD = 48;
const DOUBLE_TAP_DELAY = 320;
const trackSet = new WeakSet();
const lightboxSet = new WeakSet();
const lightboxStates = new WeakMap();

function getLightboxState(dialog) {
  if (!lightboxStates.has(dialog)) {
    lightboxStates.set(dialog, {
      images: [],
      index: 0,
      scale: MIN_ZOOM,
      x: 0,
      y: 0,
      pointers: new Map(),
      gesture: null,
      pinchDistance: 0,
      pinchScale: MIN_ZOOM,
      lastTapAt: 0,
      lastTapPoint: null,
      didPinch: false,
    });
  }

  return lightboxStates.get(dialog);
}

function updateTrack(track) {
  const section = track.closest('.teka-product-room-inspiration');
  const previousButton = section?.querySelector('[data-room-scroll="-1"]');
  const nextButton = section?.querySelector('[data-room-scroll="1"]');
  const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

  track.dataset.roomOverflow = String(maxScroll > 1);
  if (!previousButton || !nextButton) return;

  previousButton.disabled = track.scrollLeft <= 1;
  nextButton.disabled = track.scrollLeft >= maxScroll - 1;
}

function updateZoom(dialog) {
  const state = getLightboxState(dialog);
  const viewport = dialog.querySelector('[data-room-lightbox-viewport]');
  const image = dialog.querySelector('[data-room-lightbox-image] img');
  if (!viewport || !image) return;

  const maxX = Math.max(0, (image.offsetWidth * state.scale - viewport.clientWidth) / 2);
  const maxY = Math.max(0, (image.offsetHeight * state.scale - viewport.clientHeight) / 2);
  state.x = state.scale === MIN_ZOOM ? 0 : Math.min(maxX, Math.max(-maxX, state.x));
  state.y = state.scale === MIN_ZOOM ? 0 : Math.min(maxY, Math.max(-maxY, state.y));
  image.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;

  const zoomOut = dialog.querySelector('[data-room-lightbox-zoom="-1"]');
  const zoomIn = dialog.querySelector('[data-room-lightbox-zoom="1"]');
  if (zoomOut) zoomOut.disabled = state.scale <= MIN_ZOOM;
  if (zoomIn) zoomIn.disabled = state.scale >= MAX_ZOOM;
}

function updateSlide(dialog) {
  const state = getLightboxState(dialog);
  const imageContainer = dialog.querySelector('[data-room-lightbox-image]');
  const imageData = state.images[state.index];
  if (!imageContainer || !imageData) return;

  state.scale = MIN_ZOOM;
  state.x = 0;
  state.y = 0;
  state.pointers.clear();
  state.gesture = null;
  state.didPinch = false;

  const image = new Image();
  image.alt = imageData.alt;
  image.draggable = false;
  image.onload = () => updateZoom(dialog);
  image.src = imageData.src;
  imageContainer.replaceChildren(image);

  const previousButton = dialog.querySelector('[data-room-lightbox-step="-1"]');
  const nextButton = dialog.querySelector('[data-room-lightbox-step="1"]');
  const counter = dialog.querySelector('[data-room-lightbox-count]');
  if (previousButton) previousButton.disabled = state.index === 0;
  if (nextButton) nextButton.disabled = state.index === state.images.length - 1;
  if (counter) counter.textContent = `${state.index + 1} / ${state.images.length}`;
  updateZoom(dialog);
}

function stepSlide(dialog, direction) {
  const state = getLightboxState(dialog);
  const nextIndex = state.index + direction;
  if (nextIndex < 0 || nextIndex >= state.images.length) return;

  state.index = nextIndex;
  updateSlide(dialog);
}

function openLightbox(button) {
  const section = button.closest('.teka-product-room-inspiration');
  const dialog = section?.querySelector('[data-room-lightbox]');
  if (!section || !dialog) return;

  const state = getLightboxState(dialog);
  state.images = Array.from(section.querySelectorAll('[data-room-image]')).map((imageButton) => ({
    src: imageButton.dataset.roomImage,
    alt: imageButton.dataset.roomAlt || '',
  }));
  state.index = Array.from(section.querySelectorAll('[data-room-image]')).indexOf(button);
  if (state.index < 0) return;

  updateSlide(dialog);
  dialog.showModal();
}

function setZoom(dialog, scale) {
  const state = getLightboxState(dialog);
  state.scale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
  if (state.scale === MIN_ZOOM) {
    state.x = 0;
    state.y = 0;
  }
  updateZoom(dialog);
}

function getPointDistance(first, second) {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

function bindLightbox(dialog) {
  if (lightboxSet.has(dialog)) return;
  lightboxSet.add(dialog);

  const viewport = dialog.querySelector('[data-room-lightbox-viewport]');
  if (!viewport) return;

  viewport.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const state = getLightboxState(dialog);
    viewport.setPointerCapture(event.pointerId);
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (state.pointers.size === 1) {
      state.gesture = {
        x: event.clientX,
        y: event.clientY,
        startX: state.x,
        startY: state.y,
        startedAt: performance.now(),
      };
      state.didPinch = false;
    } else if (state.pointers.size === 2) {
      const [first, second] = state.pointers.values();
      state.pinchDistance = getPointDistance(first, second);
      state.pinchScale = state.scale;
      state.didPinch = true;
    }
  });

  viewport.addEventListener('pointermove', (event) => {
    const state = getLightboxState(dialog);
    if (!state.pointers.has(event.pointerId)) return;
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (state.pointers.size >= 2) {
      const [first, second] = state.pointers.values();
      const distance = getPointDistance(first, second);
      if (state.pinchDistance > 0) {
        state.scale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, state.pinchScale * (distance / state.pinchDistance)));
        updateZoom(dialog);
      }
      return;
    }

    if (state.scale > MIN_ZOOM && state.gesture) {
      state.x = state.gesture.startX + event.clientX - state.gesture.x;
      state.y = state.gesture.startY + event.clientY - state.gesture.y;
      updateZoom(dialog);
    }
  });

  const finishPointer = (event) => {
    const state = getLightboxState(dialog);
    const point = state.pointers.get(event.pointerId);
    if (!point) return;
    state.pointers.delete(event.pointerId);

    if (state.pointers.size === 1 && state.didPinch) {
      const [remaining] = state.pointers.values();
      state.gesture = { x: remaining.x, y: remaining.y, startX: state.x, startY: state.y, startedAt: performance.now() };
      return;
    }

    if (state.pointers.size > 0) return;

    const gesture = state.gesture;
    state.gesture = null;
    if (!gesture || state.didPinch) return;

    const deltaX = point.x - gesture.x;
    const deltaY = point.y - gesture.y;
    if (state.scale === MIN_ZOOM && Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
      stepSlide(dialog, deltaX < 0 ? 1 : -1);
      return;
    }

    if (Math.hypot(deltaX, deltaY) > 12 || performance.now() - gesture.startedAt > DOUBLE_TAP_DELAY) return;
    const previousTap = state.lastTapPoint;
    const isDoubleTap = previousTap
      && performance.now() - state.lastTapAt < DOUBLE_TAP_DELAY
      && getPointDistance(previousTap, point) < 36;

    if (isDoubleTap) {
      setZoom(dialog, state.scale > MIN_ZOOM ? MIN_ZOOM : 2.5);
      state.lastTapAt = 0;
      state.lastTapPoint = null;
    } else {
      state.lastTapAt = performance.now();
      state.lastTapPoint = point;
    }
  };

  viewport.addEventListener('pointerup', finishPointer);
  viewport.addEventListener('pointercancel', finishPointer);

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      stepSlide(dialog, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      stepSlide(dialog, 1);
    }
  });

  dialog.addEventListener('close', () => setZoom(dialog, MIN_ZOOM));
}

function initialize(root = document) {
  const tracks = [];
  const dialogs = [];
  if (root.matches?.('[data-room-track]')) tracks.push(root);
  if (root.matches?.('[data-room-lightbox]')) dialogs.push(root);
  tracks.push(...root.querySelectorAll('[data-room-track]'));
  dialogs.push(...root.querySelectorAll('[data-room-lightbox]'));

  tracks.forEach((track) => {
    if (!trackSet.has(track)) {
      track.addEventListener('scroll', () => updateTrack(track), { passive: true });
      trackSet.add(track);
    }
    updateTrack(track);
  });
  dialogs.forEach(bindLightbox);
}

document.addEventListener('click', (event) => {
  const closeButton = event.target.closest('[data-room-lightbox-close]');
  if (closeButton) {
    closeButton.closest('[data-room-lightbox]')?.close();
    return;
  }

  const imageButton = event.target.closest('[data-room-image]');
  if (imageButton) {
    openLightbox(imageButton);
    return;
  }

  const stepButton = event.target.closest('[data-room-lightbox-step]');
  if (stepButton) {
    const dialog = stepButton.closest('[data-room-lightbox]');
    if (dialog) stepSlide(dialog, Number(stepButton.dataset.roomLightboxStep));
    return;
  }

  const zoomButton = event.target.closest('[data-room-lightbox-zoom]');
  if (zoomButton) {
    const dialog = zoomButton.closest('[data-room-lightbox]');
    if (dialog) {
      const state = getLightboxState(dialog);
      setZoom(dialog, state.scale + Number(zoomButton.dataset.roomLightboxZoom) * 0.5);
    }
    return;
  }

  if (event.target.matches('[data-room-lightbox]')) {
    event.target.close();
    return;
  }

  const button = event.target.closest('[data-room-scroll]');
  if (!button) return;

  const section = button.closest('.teka-product-room-inspiration');
  const track = section?.querySelector('[data-room-track]');
  const firstCard = track?.querySelector('.teka-product-room-inspiration__card');
  if (!track || !firstCard) return;

  const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  track.scrollBy({
    left: Number(button.dataset.roomScroll) * (firstCard.getBoundingClientRect().width + gap),
    behavior: reduceMotion ? 'auto' : 'smooth',
  });
});

initialize();
window.addEventListener('resize', () => initialize());
document.addEventListener('shopify:section:load', (event) => initialize(event.target));
